import type { ReactElement } from 'react';

import { monitorLogCardItemClasses } from './agentLogDrawerAccordionStyles';

import type { AgentExecutionLogEntry } from '~/api/agentLogs/types';
import { ConfigurationCard } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/components/ConfigurationCard';
import { GuardrailCard } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/components/GuardrailCard';
import { InputCard } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/components/InputCard';
import { MessageCard } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/components/MessageCard';
import { OutputMessageCard } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/components/OutputMessageCard';
import { LogCard } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/components/shared';
import { ToolCallCard } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/components/ToolCallCard';
import {
  isAgentErrorLog,
  parseAgentExecutionError,
  parseAgentInitConfig,
  parseAgentInputAddedConfig,
  parseAgentInputMessageConfig,
  parseAgentOutputMessageConfig,
  parseAgentToolCallConfig,
  parseGuardrailConfig
} from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/utils/logParser';

type RenderMonitorExecutionLogCardParams = {
  log: AgentExecutionLogEntry;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  nestedAccordionSyncKey?: number;
  nestedAccordionSyncExpanded?: boolean;
};

function MonitorErrorLogCard({
  log,
  isExpanded,
  onExpandedChange,
  itemKey
}: {
  log: AgentExecutionLogEntry;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  itemKey: string;
}) {
  const { title: errorTitle, message: errorMessage } = parseAgentExecutionError(
    log.body
  );

  return (
    <LogCard
      title={
        <div className='flex items-center gap-2'>
          <span className='text-sm font-semibold text-negative'>
            {errorTitle}
          </span>
          <span className='rounded-full bg-negative/10 px-2 py-0.5 text-xs font-medium text-negative'>
            ERROR
          </span>
        </div>
      }
      textValue={errorTitle}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      itemKey={itemKey}
      itemClasses={monitorLogCardItemClasses}
      className=''
    >
      <div className='rounded-lg border border-negative/30 bg-negative/10 p-4'>
        <p className='text-sm font-medium text-negative'>{errorMessage}</p>
      </div>
    </LogCard>
  );
}

export function renderMonitorExecutionLogCard({
  log,
  isExpanded,
  onExpandedChange,
  nestedAccordionSyncKey,
  nestedAccordionSyncExpanded
}: RenderMonitorExecutionLogCardParams): ReactElement | null {
  const logMessage = log.body?.trim();
  if (!logMessage) {
    return null;
  }

  const cardProps = {
    isExpanded,
    onExpandedChange,
    itemKey: log.id,
    itemClasses: monitorLogCardItemClasses,
    className: ''
  };

  const nestedAccordionProps = {
    nestedAccordionSyncKey,
    nestedAccordionSyncExpanded
  };

  const initConfig = parseAgentInitConfig(logMessage);
  if (initConfig) {
    return <ConfigurationCard config={initConfig} {...cardProps} />;
  }

  const inputConfig = parseAgentInputAddedConfig(logMessage);
  if (inputConfig) {
    return <InputCard config={inputConfig} {...cardProps} />;
  }

  const messageConfig = parseAgentInputMessageConfig(logMessage);
  if (messageConfig) {
    return <MessageCard config={messageConfig} {...cardProps} />;
  }

  const guardrailConfig = parseGuardrailConfig(logMessage);
  if (guardrailConfig) {
    return (
      <GuardrailCard
        config={guardrailConfig}
        {...cardProps}
        {...nestedAccordionProps}
      />
    );
  }

  const toolCallConfig = parseAgentToolCallConfig(logMessage);
  if (toolCallConfig) {
    return (
      <ToolCallCard
        config={toolCallConfig}
        {...cardProps}
        {...nestedAccordionProps}
      />
    );
  }

  const outputMessageConfig = parseAgentOutputMessageConfig(logMessage);
  if (outputMessageConfig) {
    return <OutputMessageCard config={outputMessageConfig} {...cardProps} />;
  }

  if (isAgentErrorLog(logMessage)) {
    return (
      <MonitorErrorLogCard
        log={log}
        isExpanded={isExpanded}
        onExpandedChange={onExpandedChange}
        itemKey={log.id}
      />
    );
  }

  return null;
}
